import { Box, Button, HStack, Tag, Text, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import Select from "../../../../../shared/components/Select";
import type { CaregiverId, OfficeId } from "../../../../../shared/schema/schema";
import EntityProfileEditModeButton from "../../../../entityProfileModal/components/EntityProfileEditModeButton";
import {
  EntityProfileErrorState,
  EntityProfileLoadingState,
} from "../../../../entityProfileModal/components/EntityProfileFeedback";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import { useCaregiverProfileValidatedDutiesController } from "./CaregiverProfileValidatedDutiesSection.hooks";

type Props = {
  caregiverId: CaregiverId;
  /** The caregiver's offices; undefined while the profile is still loading. */
  officeIds: OfficeId[] | undefined;
};

/**
 * The plan-of-care duties this caregiver is validated to perform, agency-wide.
 *
 * Only matters for duties a patient's plan marks "only validated caregivers":
 * those are listed on the caregiver's visits only when the duty is in this set.
 * Every other duty is open to every caregiver regardless of what is here.
 *
 * Mounted both by the React caregiver profile and, through the react adapter,
 * by the legacy AngularJS profile, so it takes plain ids rather than the React
 * profile's caregiver object.
 */
export default function CaregiverProfileValidatedDutiesSection(props: Props) {
  const controller = useCaregiverProfileValidatedDutiesController(
    props.caregiverId,
    props.officeIds
  );
  const { validationsQuery } = controller;

  if (validationsQuery.isLoading || props.officeIds === undefined) {
    return <EntityProfileLoadingState label="Loading validated duties" />;
  }

  if (validationsQuery.isError || controller.validatedItemIds === undefined) {
    return (
      <EntityProfileErrorState
        label="Could not load validated duties."
        onRetry={() => controller.refetch()}
      />
    );
  }

  const validatedItemIds = controller.validatedItemIds;
  const selected = controller.draft ?? validatedItemIds;
  const labelOf = (itemId: typeof selected[number]) =>
    controller.options.find((option) => option.value === itemId)?.label ?? `#${itemId}`;

  return (
    <VStack alignItems="stretch" spacing={5}>
      <EntityProfileEditModeButton
        isDisabled={controller.isUpdating}
        isEditing={controller.isEditing}
        label="Edit"
        onClick={controller.toggleEditMode}
      />

      <Text color={entityProfileModalColors.muted} fontSize="14px">
        Duties a plan of care marks as &quot;only validated caregivers&quot; appear on this
        caregiver&apos;s visits only if they are listed here. All other duties are unaffected.
      </Text>

      {controller.isEditing ? (
        <VStack alignItems="flex-start" spacing={3}>
          <Box maxW="480px" w="100%">
            <Select
              isLoading={controller.isLoadingOptions}
              label="Select duties"
              multiple={true}
              options={controller.options}
              searchable={true}
              selectedLabel={(value) =>
                value.length === 0 ? "No validated duties" : `Validated duties (${value.length})`
              }
              value={selected}
              onChange={(value) => controller.setDraft(value ?? [])}
            />
          </Box>
          <HStack>
            <Button
              colorScheme="blue"
              isLoading={controller.isUpdating}
              size="sm"
              onClick={() => controller.save(selected)}
            >
              Save
            </Button>
            <Button
              isDisabled={controller.isUpdating}
              size="sm"
              variant="ghost"
              onClick={controller.toggleEditMode}
            >
              Cancel
            </Button>
          </HStack>
        </VStack>
      ) : validatedItemIds.length === 0 ? (
        <Text color={entityProfileModalColors.text} fontSize="14px">
          No validated duties. Restricted duties are hidden from this caregiver&apos;s visits.
        </Text>
      ) : (
        <Wrap spacing={2}>
          {validatedItemIds.map((itemId) => (
            <WrapItem key={itemId}>
              <Tag colorScheme="blue" size="md">
                {labelOf(itemId)}
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}
    </VStack>
  );
}
